import { useState } from "react";
import { useTranslation } from "react-i18next";
import { CopyIcon, DotsThreeIcon, LockSimpleIcon, TrashIcon } from "@phosphor-icons/react";
import { Button } from "@libs/zapi-ui/ds/components/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from "@libs/zapi-ui/ds/components/dropdown-menu";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import { getNameInitials } from "@/zapi-frontend/shared/lib/getNameInitials";
import type { TProject } from "@/zapi-frontend/stores/project";
import type { TOrganizationProjectDimension } from "@/zapi-frontend/shared/types/projects";
import { ProjectIcon } from "@/zapi-frontend/shared/ui/ProjectIconEditor/ProjectIconEditor";
import { useFeatureFlag } from "@/zapi-frontend/shared/hooks/useFeatureFlag";
import type { TSystemColumn } from "../../lib/portfolioStorage";
import { formatUpdatedAgo } from "../../lib/formatUpdatedAgo";
import { getProjectStatus, getProjectStatusLabel, type TProjectStatus } from "../../lib/projectStatus";
import { VersionMenu, glassControl } from "./_VersionMenu";
import { Avatar, AvatarFallback } from "@libs/zapi-ui/ds/components/avatar";

type TProps = {
  project: TProject;
  visibleDimensions: TOrganizationProjectDimension[];
  hiddenSystemColumns: Set<TSystemColumn>;
  hasEdit: boolean;
  canDuplicateProject: boolean;
  organizationId: number;
  onListInvalidated: () => void;
  onOpenProject: (versionId?: string) => void;
  onDuplicateProject: (id: string) => void;
  onDeleteProject: (id: string) => void;
  isDeleting?: boolean;
};

const statusTone: Record<TProjectStatus["state"], string> = {
  complete: "bg-success/10 text-success",
  generating: "bg-secondary text-secondary-foreground",
  framing: "border border-border text-foreground",
  failed: "bg-destructive/10 text-destructive"
};

export function ProjectCard({
  project,
  visibleDimensions,
  hiddenSystemColumns,
  hasEdit,
  canDuplicateProject,
  organizationId,
  onListInvalidated,
  onOpenProject,
  onDuplicateProject,
  onDeleteProject,
  isDeleting
}: TProps) {
  const { t } = useTranslation("projects");
  const { t: tHome } = useTranslation("projectHome");
  const [selectedVersionId, setSelectedVersionId] = useState(project.id);
  const selectedVersion = project.versions?.find((v) => v.id === selectedVersionId);
  const claudeCodeGeneration = useFeatureFlag("claude-code-generation", organizationId) ?? false;
  const status = getProjectStatus(selectedVersion ?? project);
  const statusLabel = getProjectStatusLabel(t, selectedVersion ?? project, claudeCodeGeneration);

  const values = project.dimensionValues ?? {};
  const tags = visibleDimensions.map((d) => values[String(d.id)] ?? "").filter(Boolean);
  const modifiedBy = selectedVersion?.modifiedBy || project.modifiedBy || "";
  const updated = formatUpdatedAgo(selectedVersion?.lastModified ?? project.lastModified);
  const showDuplicate = canDuplicateProject && !project.isDraft;

  return (
    <article
      data-testid="project-card-v2"
      className={cn(
        "group/project relative isolate flex h-[160px] w-full overflow-hidden rounded-2xl bg-card shadow-xs ring-1 ring-foreground/5",
        "transition-[box-shadow,translate] duration-300 ease-out hover:-translate-y-0.5 hover:shadow-lg",
        "has-[[data-open-project]:focus-visible]:ring-3 has-[[data-open-project]:focus-visible]:ring-ring/50",
        isDeleting && "pointer-events-none opacity-50"
      )}
    >
      <div className="relative h-full w-[120px] shrink-0 overflow-hidden sm:w-48">
        <ProjectIcon
          shape={project.iconShape}
          color={project.iconColor}
          logoUrl={project.logoUrl}
          className="absolute inset-0 size-full transition-transform duration-700 ease-out group-hover/project:scale-[1.04]"
        />
        {!hiddenSystemColumns.has("version") && (
          <div className="absolute start-2 top-2 z-10 flex max-w-[calc(100%-1rem)]">
            <VersionMenu
              project={project}
              hasEdit={hasEdit}
              organizationId={organizationId}
              selectedVersionId={selectedVersionId}
              onSelectVersion={setSelectedVersionId}
              onListInvalidated={onListInvalidated}
            />
          </div>
        )}
      </div>

      <div className="flex min-w-0 flex-1 flex-col justify-between py-3.5 ps-4 pe-3 sm:ps-5">
        <div className="flex min-w-0 flex-col gap-1 pe-9">
          <div className="flex h-8 min-w-0 items-center gap-2">
            <h3 className="typo-label-lg truncate text-foreground">{project.name}</h3>
            {project.visibility === "private" && (
              <LockSimpleIcon aria-label={t("card.private")} className="size-3.5 shrink-0 text-muted-foreground" />
            )}
            <span
              className={cn(
                "typo-label-sm inline-flex h-5 shrink-0 items-center rounded-full px-2 whitespace-nowrap",
                statusTone[status.state]
              )}
            >
              {statusLabel}
            </span>
          </div>
          {project.shortDescription && (
            <p className="typo-body-sm line-clamp-2 text-muted-foreground">{project.shortDescription}</p>
          )}
        </div>
        <div className="flex min-w-0 items-center gap-3">
          {tags.length > 0 && (
            <ul className="flex min-w-0 flex-1 gap-1.5 overflow-hidden mask-[linear-gradient(to_right,black_85%,transparent)]">
              {tags.map((tag, index) => (
                <li
                  key={index}
                  className="typo-caption flex h-6 shrink-0 items-center rounded-full bg-greyscale-100 px-2.5 font-medium text-greyscale-700"
                >
                  {tag}
                </li>
              ))}
            </ul>
          )}
          <div className="ms-auto flex min-w-0 shrink-0 items-center gap-1.5">
            {modifiedBy && (
              <Avatar className="hidden size-6 sm:flex" title={modifiedBy}>
                <AvatarFallback className="text-muted-foreground">{getNameInitials(modifiedBy)}</AvatarFallback>
              </Avatar>
            )}
            <span className="typo-caption truncate text-muted-foreground">
              {tHome("overview.updated", { date: updated })}
              {modifiedBy && (
                <span className="hidden sm:inline">
                  {" · "}
                  <span className="text-foreground">{modifiedBy}</span>
                </span>
              )}
            </span>
          </div>
        </div>
      </div>

      {hasEdit && (
        <div className="absolute end-3 top-3 z-10">
          <DropdownMenu>
            <DropdownMenuTrigger
              data-testid={`project-${project.id}-options-trigger`}
              render={
                <Button
                  variant="outline"
                  size="icon-xs"
                  shape="pill"
                  aria-label={t("card.actions", { name: project.name })}
                  className={cn("size-7", glassControl)}
                />
              }
            >
              <DotsThreeIcon weight="bold" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" sideOffset={8} className="w-48">
              {showDuplicate && (
                <>
                  <DropdownMenuItem
                    data-testid={`project-${project.id}-duplicate-item`}
                    onClick={() => onDuplicateProject(project.id)}
                  >
                    <CopyIcon />
                    {t("card.duplicate")}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                </>
              )}
              <DropdownMenuItem
                variant="destructive"
                data-testid={`project-${project.id}-delete-item`}
                onClick={() => onDeleteProject(project.id)}
              >
                <TrashIcon />
                {t("card.delete")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}

      <button
        type="button"
        data-open-project
        data-testid={`project-${project.id}-open-button`}
        aria-label={`${t("card.open")} ${project.name}`}
        onClick={() => onOpenProject(selectedVersionId)}
        className="absolute inset-0 z-0 cursor-pointer outline-none"
      />
    </article>
  );
}
